import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api/client'
import { Plus, Search, Edit2, Trash2, Eye } from 'lucide-react'

interface Game {
  id: string
  name: string
  title: string
  status: 'active' | 'hidden' | 'deleted'
  category: string
  tags: string[]
  viewCount: number
  cover?: string
  icon?: string
  images?: {
    cover?: { primary?: string }
    icon?: { primary?: string }
  }
}

export default function Games() {
  const [games, setGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    fetchGames()
  }, [])

  const fetchGames = async () => {
    try {
      const res = await api.get('/admin/games')
      setGames(res.data.data.games)
    } catch (error) {
      console.error('Failed to fetch games:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这个游戏吗？')) return
    try {
      await api.delete(`/admin/games/${id}`)
      fetchGames()
    } catch (error) {
      console.error('Failed to delete game:', error)
    }
  }

  const filteredGames = games.filter(game => {
    const matchesSearch = game.name.toLowerCase().includes(search.toLowerCase()) ||
                         game.title.toLowerCase().includes(search.toLowerCase())
    const matchesFilter = filter === 'all' || game.status === filter
    return matchesSearch && matchesFilter
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">游戏管理</h1>
          <p className="text-muted-foreground">管理所有游戏信息</p>
        </div>
        <Link
          to="/games/new"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">新建游戏</span>
        </Link>
      </div>

      {/* 搜索和筛选 */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="搜索游戏..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">全部状态</option>
          <option value="active">上线</option>
          <option value="hidden">隐藏</option>
        </select>
      </div>

      {/* 游戏列表 */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="divide-y divide-border">
          {filteredGames.map((game) => (
            <div
              key={game.id}
              className="flex items-center gap-4 p-4 hover:bg-secondary/50 transition-colors"
            >
              {/* 图标 */}
              <div className="w-12 h-12 rounded-lg bg-secondary flex-shrink-0 overflow-hidden">
                {(() => {
                  const iconUrl = game.icon || game.images?.icon?.primary
                  return iconUrl ? (
                    <img
                      src={`/image/${game.id}/icon`}
                      alt={game.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = iconUrl
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Eye className="w-5 h-5" />
                    </div>
                  )
                })()}
              </div>

              {/* 信息 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium truncate">{game.title}</h3>
                  <StatusBadge status={game.status} />
                </div>
                <p className="text-sm text-muted-foreground truncate">
                  {game.name} · {game.category} · 浏览 {game.viewCount}
                </p>
              </div>

              {/* 操作 */}
              <div className="flex items-center gap-1">
                <Link
                  to={`/games/${game.id}`}
                  className="p-2 rounded-lg hover:bg-secondary transition-colors"
                  title="编辑"
                >
                  <Edit2 className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => handleDelete(game.id)}
                  className="p-2 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
                  title="删除"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {filteredGames.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              {search ? '没有找到匹配的游戏' : '暂无游戏，点击右上角新建'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const styles = {
    active: 'bg-green-500/10 text-green-500',
    hidden: 'bg-yellow-500/10 text-yellow-500',
    deleted: 'bg-red-500/10 text-red-500'
  }

  const labels = {
    active: '上线',
    hidden: '隐藏',
    deleted: '已删除'
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs ${styles[status as keyof typeof styles]}`}>
      {labels[status as keyof typeof labels]}
    </span>
  )
}
